import apiClient from './client';

export interface AssessmentQuestion {
  id: string;
  text: string;
  type: 'yes_no' | 'scale' | 'multiple_choice';
  options?: string[];
  required: boolean;
}

export interface AssessmentAnswer {
  questionId: string;
  answer: any;
  timestamp: Date;
}

export interface AssessmentStepData {
  stepId: string;
  title: string;
  description: string;
  questions: AssessmentQuestion[];
  answers: AssessmentAnswer[];
  completed: boolean;
  score: number;
}

export interface OverallScore {
  score: number;
  percentage: number;
  status: string;
  category: string;
}

export interface HealthAssessment {
  id: string;
  birdId: string;
  birdName: string;
  assessmentDate: string;
  status: 'IN_PROGRESS' | 'COMPLETED' | 'CANCELLED';
  assessmentSteps: AssessmentStepData[];
  overallScore?: OverallScore;
  answers: AssessmentAnswer[];
  notes?: string;
  recommendations: string[];
  actionItems: string[];
  completedBy?: string;
  completedAt?: string;
  durationMinutes?: number;
  followUpRequired: string[];
  nextAssessmentDue?: string;
  createdAt: string;
  updatedAt: string;
}

export interface CreateHealthAssessmentDto {
  birdId: string;
  birdName: string;
  assessmentDate: string;
  assessmentSteps: AssessmentStepData[];
  notes?: string;
}

export interface UpdateHealthAssessmentDto {
  assessmentSteps?: AssessmentStepData[];
  status?: 'IN_PROGRESS' | 'COMPLETED' | 'CANCELLED';
  notes?: string;
}

export interface AssessmentTrends {
  trends: Array<{
    date: string;
    score: number;
    percentage: number;
    status: string;
    category: string;
  }>;
  averageScore: number;
  totalAssessments: number;
  completionRate: number;
}

export interface FlockAssessmentSummary {
  totalAssessments: number;
  averageScore: number;
  healthDistribution: {
    excellent: number;
    good: number;
    fair: number;
    poor: number;
  };
  recentTrends: {
    recentAverage: number;
    olderAverage: number;
    trend: 'improving' | 'declining' | 'stable';
    change: number;
  };
  topConcerns: string[];
  stepCompletion: {
    visual: { completed: number; total: number };
    behavioral: { completed: number; total: number };
    physical: { completed: number; total: number };
    environmental: { completed: number; total: number };
  };
}

export const healthAssessmentsApi = {
  // Create a new health assessment
  create: async (data: CreateHealthAssessmentDto): Promise<HealthAssessment> => {
    const response = await apiClient.post<HealthAssessment>('/health-assessments', data);
    return response.data;
  },

  // Get all health assessments
  getAll: async (params?: {
    birdId?: string;
    status?: 'IN_PROGRESS' | 'COMPLETED' | 'CANCELLED';
  }): Promise<HealthAssessment[]> => {
    const response = await apiClient.get<HealthAssessment[]>('/health-assessments', params);
    return response.data;
  },

  // Get a specific health assessment
  getById: async (id: string): Promise<HealthAssessment> => {
    const response = await apiClient.get<HealthAssessment>(`/health-assessments/${id}`);
    return response.data;
  },

  // Update a health assessment
  update: async (id: string, data: UpdateHealthAssessmentDto): Promise<HealthAssessment> => {
    const response = await apiClient.patch<HealthAssessment>(`/health-assessments/${id}`, data);
    return response.data;
  },

  // Delete a health assessment
  delete: async (id: string): Promise<void> => {
    await apiClient.delete(`/health-assessments/${id}`);
  },

  // Complete a specific assessment step
  completeStep: async (id: string, stepId: string, answers: AssessmentAnswer[]): Promise<HealthAssessment> => {
    const response = await apiClient.patch<HealthAssessment>(`/health-assessments/${id}/complete-step/${stepId}`, {
      answers
    });
    return response.data;
  },

  // Get assessment trends for a specific bird
  getAssessmentTrends: async (birdId: string, days: number = 30): Promise<AssessmentTrends> => {
    const response = await apiClient.get<AssessmentTrends>(`/health-assessments/bird/${birdId}/trends`, { days: days.toString() });
    return response.data;
  },

  // Get flock assessment summary
  getFlockAssessmentSummary: async (): Promise<FlockAssessmentSummary> => {
    const response = await apiClient.get<FlockAssessmentSummary>('/health-assessments/flock/summary');
    return response.data;
  },

  // Get assessments by bird
  getByBird: async (birdId: string): Promise<HealthAssessment[]> => {
    const response = await apiClient.get<HealthAssessment[]>('/health-assessments', { birdId });
    return response.data;
  },

  // Complete an assessment
  complete: async (id: string, notes?: string): Promise<HealthAssessment> => {
    return healthAssessmentsApi.update(id, {
      status: 'COMPLETED',
      notes
    });
  },

  // Get recent assessments
  getRecent: async (limit: number = 10): Promise<HealthAssessment[]> => {
    const response = await apiClient.get<HealthAssessment[]>('/health-assessments', { limit: limit.toString() });
    return response.data;
  },

  // Get pending assessments
  getPending: async (): Promise<HealthAssessment[]> => {
    const response = await apiClient.get<HealthAssessment[]>('/health-assessments', { status: 'IN_PROGRESS' });
    return response.data;
  }
};
